import React from "react";
import { Button } from "./button";

interface FooterProps {
  logo: React.ReactNode;
  brandName: string;
  socialLinks: Array<{
    icon: React.ReactNode;
    href: string;
    label: string;
  }>;
  mainLinks: Array<{
    href: string;
    label: string;
  }>;
  legalLinks: Array<{
    href: string;
    label: string;
  }>;
  copyright: {
    text: string;
    license?: string;
  };
}

export function Footer({
  logo,
  brandName,
  socialLinks,
  mainLinks,
  legalLinks,
  copyright,
}: FooterProps) {
  return (
    <footer className="relative mt-16 overflow-hidden bg-gradient-to-br from-[#041833] via-[#052049] to-[#0a306c] text-slate-50">
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        aria-hidden="true"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 20%, rgba(52,152,219,0.28), transparent 35%), radial-gradient(circle at 80% 0%, rgba(0,64,128,0.24), transparent 32%), radial-gradient(circle at 50% 80%, rgba(255,255,255,0.08), transparent 40%)',
        }}
      />
      <div className="relative max-w-6xl mx-auto px-6 py-14 lg:px-10 lg:py-16">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-md space-y-4">
            <div className="flex items-center gap-3">
              {logo}
              <div>
                <div className="font-semibold text-xl leading-tight">{brandName}</div>
                <p className="text-slate-200 text-sm">
                  Leveling the playing field for college access through mentorship and community.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              {socialLinks.map((link, i) => (
                <Button
                  key={i}
                  variant="ghost"
                  size="icon"
                  className="h-11 w-11 rounded-full border border-white/20 bg-white/10 text-white hover:bg-white/20 hover:text-white"
                  asChild
                >
                  <a href={link.href} target="_blank" aria-label={link.label} rel="noreferrer">
                    {link.icon}
                  </a>
                </Button>
              ))}
            </div>
          </div>

          <div className="grid w-full gap-8 sm:grid-cols-2 lg:w-auto lg:grid-cols-3">
            <div>
              <p className="text-sm font-semibold tracking-wide text-slate-200 uppercase mb-3">
                Explore
              </p>
              <ul className="space-y-2">
                {mainLinks.map((link, i) => (
                  <li key={i}>
                    <a
                      href={link.href}
                      className="text-sm text-slate-100 underline-offset-4 hover:text-primary hover:underline transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-sm font-semibold tracking-wide text-slate-200 uppercase mb-3">
                More
              </p>
              <ul className="space-y-2">
                {legalLinks.map((link, i) => (
                  <li key={i}>
                    <a
                      href={link.href}
                      className="text-sm text-slate-100 underline-offset-4 hover:text-primary hover:underline transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-2 text-sm text-slate-200">
              <p className="font-semibold text-slate-50">Get in touch</p>
              <p>staff@projectlevelthefield.com</p>
              <p>(908) 842-4657</p>
              <p className="pt-2 text-slate-300">{copyright.text}</p>
              {copyright.license && <p className="text-slate-400">{copyright.license}</p>}
            </div>
          </div>
        </div>

        <div className="mt-10 border-t border-white/10 pt-5 text-sm text-slate-300 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          <span>Building equitable pathways to higher education.</span>
          <span className="text-slate-400">
            Questions? <a href="mailto:staff@projectlevelthefield.com" className="text-slate-100 underline-offset-4 hover:underline">Email us</a>
          </span>
        </div>
      </div>
    </footer>
  );
}
